import { prop } from 'ramda';
import { useMemo, useCallback } from 'react';
import { useUserSelector } from 'js/store/hooks';

import { CURRENCIES } from '../constants';
import { useGetConfig } from './useGetConfig';
import { useNumberFormatter } from './useNumberFormatter';

export const useCurrencyFormatter = () => {
    const getConfig = useGetConfig();
    const { formatNumber } = useNumberFormatter();
    const userCurrencyCode = useUserSelector(prop('currencyCode'));

    const getCurrencySymbol = useCallback((options) => {
        const currencyCode = typeof options === 'object'
            ? options.currencyCode || userCurrencyCode
            : options || userCurrencyCode;

        return CURRENCIES[currencyCode?.toUpperCase()] || currencyCode?.toUpperCase() || '';
    }, [userCurrencyCode]);

    const formatCurrency = useCallback((amount, options = {}, locale) => {
        const { currencyCode = userCurrencyCode } = options;
        const opts = {
            ...getConfig(options.locale || locale).currency,
            ...options
        };

        const formattedCurrency = [
            getCurrencySymbol({ currencyCode }),
            formatNumber(amount, opts, locale)
        ];

        return (
            opts.symbolAtTheLeft
                ? formattedCurrency
                : formattedCurrency.reverse()
        ).join(' ').trim();
    }, [getConfig, formatNumber, getCurrencySymbol, userCurrencyCode]);

    const getIsCurrencySymbolAtTheLeft = useCallback((options = {}) => {
        const opts = {
            ...getConfig(options.locale).currency,
            ...options
        };
        const { symbolAtTheLeft = true } = opts;

        return symbolAtTheLeft;
    }, [getConfig]);

    return useMemo(() => ({
        formatCurrency,
        getCurrencySymbol,
        getIsCurrencySymbolAtTheLeft
    }), [formatCurrency, getCurrencySymbol, getIsCurrencySymbolAtTheLeft]);
};

